// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, ChevronDown, ChevronUp, Shield, Phone, ExternalLink } from 'lucide-react';

const TRUFFE = [
  {
    id: 'trading-miracoloso',
    title: 'Trading online "garantito"',
    rischio: 'Molto alto',
    desc: 'Bot automatici, "segnali sicuri", corsi segreti che promettono rendimenti del 20-30% al mese. Funzionano raccogliendo denaro fino a sparire.',
    segnali: [
      '"Garantito senza rischi" — non esiste in finanza',
      'Pressione a investire subito ("offerta limitata")',
      'Screenshot di guadagni stratosferici',
      'Nessuna licenza CONSOB o FCA verificabile',
      'Influencer pagati per promuovere',
    ],
    cosa_fare: 'Verifica sempre la licenza su consob.it. Un investimento legittimo non ha mai bisogno di pressioni temporali.',
    color: '#E84040',
  },
  {
    id: 'crypto-miracolosa',
    title: 'Criptovalute "miracolose"',
    rischio: 'Alto',
    desc: 'Nuovi token con promesse di rendimenti astronomici in poche settimane. Spesso usano lo schema pump-and-dump: comprano massicciamente, alzano il prezzo, poi vendono tutto.',
    segnali: [
      'Token mai sentito con promesse di "10x in 30 giorni"',
      'Team anonimo o irrintracciabile',
      'Rendimenti fissi garantiti in criptovaluta',
      'FOMO: "Manca poco al lancio, entra ora!"',
      'Promozioni via influencer social (spesso pagati)',
    ],
    cosa_fare: 'Le criptovalute legittime non promettono rendimenti fissi. Investi solo quello che puoi permetterti di perdere completamente.',
    color: '#E87040',
  },
  {
    id: 'ponzi',
    title: 'Schema Ponzi / piramidale',
    rischio: 'Molto alto',
    desc: 'I "rendimenti" dei partecipanti precedenti vengono pagati con i soldi dei nuovi entrati. Funziona finché ci sono nuovi aderenti. Poi crolla inevitabilmente.',
    segnali: [
      'Rendimenti costanti e alti indipendentemente dai mercati',
      'Difficoltà o impossibilità di ritirare i fondi',
      'Struttura multi-livello: guadagni reclutando altri',
      'Nessuna chiara spiegazione di come vengono generati i rendimenti',
    ],
    cosa_fare: 'Se qualcuno ti dice "investi €5.000 e guadagni €500/mese garantiti", è quasi certamente uno schema Ponzi. Denuncia a CONSOB.',
    color: '#E84040',
  },
  {
    id: 'vishing',
    title: 'Vishing (truffe telefoniche)',
    rischio: 'Alto',
    desc: 'Chiamate da sedicenti dipendenti bancari, CONSOB, Guardia di Finanza o Banca d\'Italia. Ti avvisano che il tuo conto è "compromesso" e devi spostare i soldi urgentemente.',
    segnali: [
      'Chiamata inaspettata da numero che sembra ufficiale (spoofing)',
      'Urgenza: "Deve agire subito o perde tutto"',
      'Richiedono credenziali di accesso al conto online',
      'Chiedono di spostare soldi su un "conto sicuro"',
    ],
    cosa_fare: 'Nessuna banca o autorità chiede di spostare soldi via telefono. Riattacca e chiama tu direttamente il numero ufficiale della tua banca.',
    color: '#C9A84C',
  },
  {
    id: 'immobiliare',
    title: 'Investimenti immobiliari "garantiti"',
    rischio: 'Alto',
    desc: 'Piattaforme o promotori che offrono rendimenti garantiti del 10-15% annuo su investimenti immobiliari. Spesso non hanno autorizzazioni, i progetti non esistono o i soldi vengono rubati.',
    segnali: [
      'Promesse di rendimenti fissi e garantiti sull\'immobiliare',
      'Non iscritti all\'OICVM o senza autorizzazione Banca d\'Italia',
      'Contratti vaghi o inesistenti',
      'Difficoltà a verificare il progetto fisicamente',
    ],
    cosa_fare: 'Verifica l\'iscrizione all\'Albo dei Gestori (gestorialtinativestment.bancaditalia.it). Investi solo tramite piattaforme autorizzate e regolamentate.',
    color: '#C9A84C',
  },
  {
    id: 'forex',
    title: 'Forex e CFD "facili"',
    rischio: 'Alto',
    desc: 'Broker online non regolamentati che offrono trading su valute e CFD con leva finanziaria altissima. Il 70-80% dei trader retail perde denaro su questi strumenti.',
    segnali: [
      'Broker registrati in paradisi fiscali (Cipro, Seychelles, etc.)',
      'Promesse di profitti rapidi con piccoli capitali',
      'Leva finanziaria molto alta pubblicizzata come opportunità',
      'Bonus e incentivi per depositare subito',
    ],
    cosa_fare: 'Usa solo broker autorizzati da CONSOB, FCA o BaFin. Verifica sempre su consob.it nelle liste nere.',
    color: '#E87040',
  },
];

const TruffaCard = ({ truffa }) => {
  const [open, setOpen] = useState(false);
  const rischioColor = truffa.rischio === 'Molto alto' ? '#E84040' : '#E87040';

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="sapiens-card overflow-hidden"
      data-testid={`truffe-card-${truffa.id}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left p-6"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-1">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: `${rischioColor}15` }}>
              <AlertTriangle className="w-5 h-5" style={{ color: rischioColor }} />
            </div>
            <div>
              <h3 className="font-semibold text-[#E8E8E4]">{truffa.title}</h3>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full mt-1 inline-block"
                style={{ background: `${rischioColor}15`, color: rischioColor }}>
                Rischio {truffa.rischio}
              </span>
            </div>
          </div>
          {open ? <ChevronUp className="w-5 h-5 text-[#888780] flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-[#888780] flex-shrink-0" />}
        </div>
        <p className="text-sm text-[#888780] leading-relaxed mt-4">{truffa.desc}</p>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden border-t border-white/[0.06]"
          >
            <div className="p-6 pt-4 space-y-4">
              <div>
                <div className="text-xs font-semibold text-[#E84040] uppercase tracking-wider mb-2">Segnali d'allarme</div>
                <ul className="space-y-1.5">
                  {truffa.segnali.map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#888780]">
                      <span className="text-[#E84040] mt-0.5 flex-shrink-0">✗</span>
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="p-4 rounded-xl" style={{ background: 'rgba(61,122,82,0.08)', border: '1px solid rgba(61,122,82,0.2)' }}>
                <div className="text-xs font-semibold text-[#3D7A52] uppercase tracking-wider mb-1.5">Cosa fare</div>
                <p className="text-sm text-[#888780] leading-relaxed">{truffa.cosa_fare}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const TruffeFinanziariePage = () => {
  return (
    <div className="min-h-screen bg-[#1C1C1A]">
      <Helmet>
        <title>Truffe Finanziarie — Come Riconoscerle e Difendersi | SAPIENS</title>
        <meta name="description" content="Guida completa alle truffe finanziarie più comuni in Italia: trading miracoloso, crypto, Ponzi, vishing e come difendersi." />
        <link rel="canonical" href="https://finsapiens.it/truffe-finanziarie" />
      </Helmet>

      <div className="container-wide py-10">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-2 text-sm font-medium text-[#E84040] uppercase tracking-wider mb-3">
            <AlertTriangle className="w-4 h-4" />
            Protezione finanziaria
          </div>
          <h1 className="text-4xl font-semibold text-[#E8E8E4] mb-3">Truffe finanziarie</h1>
          <p className="text-xl text-[#888780] max-w-2xl leading-relaxed">
            Conoscere le truffe è la miglior difesa. Ecco le 6 tipologie più diffuse in Italia, i segnali d'allarme e cosa fare se le incontri.
          </p>
        </motion.div>

        {/* Alert box */}
        <div className="mb-8 p-5 rounded-xl flex gap-4" style={{ background: 'rgba(232,64,64,0.06)', border: '1px solid rgba(232,64,64,0.2)' }}>
          <Shield className="w-5 h-5 text-[#E84040] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#888780] leading-relaxed">
            <strong className="text-[#E8E8E4]">Regola d'oro:</strong> In finanza, qualsiasi promessa di rendimenti garantiti e senza rischi è falsa per definizione. I rendimenti più alti esistono solo in presenza di rischi più alti — e nessuno può garantirti il futuro.
          </div>
        </div>

        {/* Truffe list */}
        <div className="space-y-4 mb-10">
          {TRUFFE.map(t => <TruffaCard key={t.id} truffa={t} />)}
        </div>

        {/* Reporting */}
        <div className="grid md:grid-cols-2 gap-5 mb-10">
          <div className="sapiens-card p-6">
            <h3 className="font-semibold text-[#E8E8E4] mb-3 flex items-center gap-2">
              <Phone className="w-4 h-4 text-[#C9A84C]" />
              Contatti utili
            </h3>
            <div className="space-y-2 text-sm text-[#888780]">
              <div>• <strong className="text-[#E8E8E4]">CONSOB:</strong> 06 8477 1 (segnalazione abusi)</div>
              <div>• <strong className="text-[#E8E8E4]">Banca d'Italia:</strong> 800.196.969 (gratuito)</div>
              <div>• <strong className="text-[#E8E8E4]">Polizia Postale:</strong> polizia-postale.poliziadi-stato.it</div>
              <div>• <strong className="text-[#E8E8E4]">Carabinieri:</strong> 112</div>
            </div>
          </div>
          <div className="sapiens-card p-6">
            <h3 className="font-semibold text-[#E8E8E4] mb-3 flex items-center gap-2">
              <ExternalLink className="w-4 h-4 text-[#C9A84C]" />
              Risorse ufficiali
            </h3>
            <div className="space-y-2 text-sm">
              <a href="https://www.consob.it/web/investor-education/lista-nera" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] hover:underline block">Lista nera CONSOB (operatori abusivi)</a>
              <a href="https://www.bancaditalia.it/compiti/vigilanza/albi-elenchi/" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] hover:underline block">Albi e elenchi Banca d'Italia</a>
              <a href="https://www.agcm.it" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] hover:underline block">AGCM (Antitrust — pubblicità ingannevole)</a>
            </div>
          </div>
        </div>

        {/* Blog CTA */}
        <div className="p-6 rounded-2xl" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)' }}>
          <div className="flex flex-col sm:flex-row items-center gap-4 justify-between">
            <div>
              <div className="font-semibold text-[#E8E8E4] mb-1">Approfondisci sul blog</div>
              <p className="text-sm text-[#888780]">Leggi l'articolo completo sulle 5 truffe più comuni in Italia nel 2026.</p>
            </div>
            <Link to="/blog/truffe-finanziarie-come-riconoscerle-2026" className="sapiens-btn-primary px-5 py-2.5 text-sm flex-shrink-0">
              Leggi l'articolo
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TruffeFinanziariePage;
